/**
 * 고객 보고서의 산출물 영역 — 우리가 그 고객에게 **만들어 올린 것**.
 * AI검색 최적화 홈페이지 · 언론 보도 · SNS 영상 · 블로그.
 * ★ 화면 문구에서 **`GEO` 를 쓰지 않는다** — 고객이 읽는 말은 `AI검색 최적화` 다 (2026-10-01).
 *   코드·주석·저장소 문서는 그대로 `GEO` 를 쓴다(식별자 `geo_work_*`·결정문 모두).
 *
 * ★ **콘텐츠 발행 섹션은 매거진(가로 레일) 조판이다** (운영자 결정 2026-10-01). 이미지가 앞서는 카드를
 *   좌→우로 늘어놓고 넘겨 본다. 산출물은 "읽을 목록"이 아니라 **보여 줄 결과물**이라는 판단이다.
 *   ⚠️ 이 선택은 `260921-client-report-chart-only` 의 *"카드·테두리·그림자를 걷어냈다"* 와 어긋난다 —
 *   그 결정의 조판 규율은 **측정 영역**에만 남기고, 산출물 영역은 다른 규율로 간다. 결정문에 적는다.
 * ★ **언론 보도만 예외다** — 레일이 아니라 큰 기사 미리보기를 세로로 쌓는다(261010-press-article-only-og, `PressPreview`).
 * ★ **AI 인용 사이트(순위) 섹션은 고객 화면에서 뺐다** (운영자 결정 2026-10-07). 데이터(`rankings`)는
 *   계약·API 에 그대로 있고 화면만 그리지 않는다 — 되살리려면 git 이력의 `RankingRow` 를 가져온다.
 * ★ **데이터를 가져오지 않는다.** props 만 받는다 — 1단계는 mock, 2단계는 `useApi` 로 호출부 한 줄만 바뀐다.
 * ★ 표본에 관한 정보는 싣지 않는다. "글 20편"·"3위"는 산출물과 관측 위치이지 표본 건수가 아니다.
 */
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PLATFORM_LABEL,
  type Platform,
  type PostView,
  type PressView,
  type ReviewView,
  type VideoView,
  videoCover,
  type WorksView,
} from '../lib/works.ts';
import {
  InstagramIcon,
  PressIcon,
  ReviewIcon,
  SiteIcon,
  ThreadsIcon,
  TikTokIcon,
  VideoIcon,
  XIcon,
  YouTubeIcon,
} from './icons.tsx';
import { sessionHeaders } from '../lib/call.ts';
import { cn } from '../lib/utils.ts';

/**
 * 섹션 — 주소 조각·라벨·아이콘·색을 **한 곳에서** 묶는다. 셋이 흩어지면 섹션을 더할 때 하나를 빠뜨린다.
 * ★ 색은 브로슈어(`docs/sales/geo-service-brochure.html`)의 다섯 색 그대로다 —
 *   화면과 영업 자료가 같은 팔레트를 쓴다.
 * ★ 측정 영역의 *"색을 가진 것은 곡선 하나뿐"* 은 **그 영역에만** 남는다
 *   (260921-client-report-chart-only 의 `[일부 번복됨]` 배너).
 */
const SECTION_META = {
  homepage: { label: 'AI검색 최적화 홈페이지', Icon: SiteIcon, color: 'text-[#2563EB]' },
  press: { label: '언론 보도', Icon: PressIcon, color: 'text-[#7C3AED]' },
  video: { label: 'SNS 영상', Icon: VideoIcon, color: 'text-[#EF4444]' },
  review: { label: '블로그', Icon: ReviewIcon, color: 'text-[#F59E0B]' },
} as const;
export type SectionKey = keyof typeof SECTION_META;
/** 라벨만 필요한 곳(섹션 전용 페이지 제목) */
export const SECTIONS = Object.fromEntries(Object.entries(SECTION_META).map(([k, v]) => [k, v.label])) as Record<
  SectionKey,
  string
>;

/** `2026-09-25` → `9/25`. ★ `Date` 로 파싱하지 않는다 — KST 날짜 문자열이라 시간대가 끼면 하루가 밀린다 */
function dateText(on: string): string {
  const [, m, d] = on.split('-');
  return `${Number(m)}/${Number(d)}`;
}

/** `https://www.x.com` → `x.com`. 저장은 **절대 URL** 이고 보일 때만 스킴·`www` 를 뗀다 */
function hostText(url: string): string {
  return url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
}

function ExternalLink({ href, children, className }: { href: string; children: string; className?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      // ★ skip-ink 를 끈다 — 기본값은 글자가 밑줄을 가로지르는 곳을 비우는데, 한글은 받침 획이
      //   거의 모든 글자에서 내려와 **밑줄이 글자마다 끊겨 보인다**(실측). 라틴 문자에서는 안 드러난다
      className={cn(
        'underline decoration-slate-300 underline-offset-2 [text-decoration-skip-ink:none]',
        'hover:decoration-slate-900',
        className,
      )}
    >
      {children}
    </a>
  );
}

/** 자리표시자 색 — 낮은 채도로 묶어 둔다. 카드가 늘어서도 화면이 소란스러워지지 않게 */
const TINTS = [
  ['#e2e8f0', '#cbd5e1'],
  ['#e7e5e4', '#d6d3d1'],
  ['#dbeafe', '#bfdbfe'],
  ['#e0e7ff', '#c7d2fe'],
  ['#ccfbf1', '#99f6e4'],
  ['#fef3c7', '#fde68a'],
] as const;

function tintOf(seed: string): readonly [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return TINTS[h % TINTS.length] as unknown as readonly [string, string];
}

/**
 * 우리 서버의 사진(`/api/…` — 언론 보도 카드의 기사 사진)은 로그인이 필요하다. `<img>` 는 헤더를 못 실어
 * 대리 탭에서 401 이 나므로, 세션 헤더를 실어 받아 blob 주소로 바꾼다. 외부 이미지는 그대로 쓴다.
 */
function useSessionImage(src: string | null): { url: string | null; failed: boolean } {
  const own = src?.startsWith('/api/') ?? false;
  const [blob, setBlob] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!own || !src) return;
    let alive = true;
    let made: string | null = null;
    setBlob(null);
    setFailed(false);
    fetch(src, { headers: sessionHeaders(), credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        made = URL.createObjectURL(await res.blob());
        // 받는 사이 카드가 사라졌으면 정리는 이미 지나갔다 — 여기서 놓아 준다
        if (alive) setBlob(made);
        else URL.revokeObjectURL(made);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [own, src]);
  return own ? { url: blob, failed } : { url: src, failed: false };
}

/**
 * 카드 대표 이미지. 실물이 없으면 **자리표시자**를 그린다 — 빈 네모를 두면 "이미지가 깨졌다"로 읽힌다.
 * 운영에서는 og:image·썸네일이 들어온다.
 */
function Cover({
  src,
  seed,
  kind,
  portrait,
  overlay,
  aspect,
  focus,
}: {
  src: string | null;
  seed: string;
  kind: string;
  portrait: boolean;
  /** 썸네일 위에 dim 과 함께 크게 얹을 것 — 주면 재생 삼각형을 대신한다 */
  overlay?: React.ReactNode;
  /** 비율 클래스 — 주면 `portrait` 의 9:16/16:9 를 대신한다(기사 미리보기의 4:3) */
  aspect?: string;
  /** 사진을 어디 기준으로 자를지(`object-position`) — 인물 사진은 위쪽 */
  focus?: string;
}) {
  // ★ 외부 사이트가 이미지를 지우면 **깨진 그림** 대신 자리표시자로 돌아간다. 실패를 기억해 두지 않으면
  //   리렌더마다 다시 시도해 같은 자리가 깜빡인다
  const [broken, setBroken] = useState(false);
  const [a, b] = tintOf(seed);
  const image = useSessionImage(src);
  const show = image.url !== null && !image.failed && !broken;
  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-md bg-slate-100',
        aspect ?? (portrait ? 'aspect-[9/16]' : 'aspect-video'),
      )}
      style={show ? undefined : { background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)` }}
    >
      {show ? (
        <img
          src={image.url ?? undefined}
          alt=""
          loading="lazy"
          // ★ 고객이 외부 사이트의 이미지를 받을 때 **우리 주소가 레퍼러로 새지 않게** 한다
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          className={cn('h-full w-full object-cover', focus)}
        />
      ) : null}
      {/* ★ 오버레이를 칩보다 **먼저** 그린다 — `inset-0` 이라 뒤에 두면 칩을 덮는다 */}
      {overlay ? (
        <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white">{overlay}</span>
      ) : portrait ? (
        <span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/80">
          <span className="ml-0.5 border-y-[6px] border-y-transparent border-l-[10px] border-l-slate-600" />
        </span>
      ) : null}
      <span className="absolute top-1.5 left-1.5 rounded bg-white/75 px-1.5 py-0.5 font-medium text-[11px] text-slate-600">
        {kind}
      </span>
    </div>
  );
}

/**
 * 카드를 늘어놓는 두 가지 — 첫 화면은 **가로 레일**(넷플릭스식, 좌우 여백까지 흘려 "더 있다"를 드러낸다),
 * 섹션 전용 페이지는 **그리드**다.
 * ★ 전용 페이지까지 레일로 두면 카드 40장이 **가로로 한없이 길어진다** — 끝까지 밀어야 마지막이 보인다.
 *   세로로 흐르는 그리드는 스크롤이 자연스럽고 한눈에 양이 보인다.
 */
function Lane({ grid, children }: { grid: boolean; children: React.ReactNode }) {
  if (grid) {
    return (
      <div className="grid gap-x-4 gap-y-6 pt-1 [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]">
        {children}
      </div>
    );
  }
  return <Carousel>{children}</Carousel>;
}

/** 넘김 버튼. ★ **누를 곳은 글자가 아니라 네모다** — 40px 정사각(261001-client-works-report) */
function LaneArrow({ dir, onClick, hidden }: { dir: 'prev' | 'next'; onClick: () => void; hidden: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'prev' ? '이전 카드' : '다음 카드'}
      // ★ `-translate-y-1/2` 가 아니라 `top-1/2` + 변환으로 카드 띠의 세로 가운데에 건다.
      //   레일의 `pt-1 pb-3` 때문에 레일 가운데가 카드 가운데보다 아래다 — 껍데기에 맞춘다.
      className={cn(
        'absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full',
        'bg-white/95 text-2xl text-slate-600 leading-none shadow-md ring-1 ring-slate-200',
        'transition-opacity hover:text-slate-900',
        // 평소에는 없다. 마우스를 레일에 올리거나 키보드로 닿으면 나타난다.
        // ★ 터치 기기에는 hover 가 없어 **저절로 안 뜬다** — 미디어 쿼리가 필요 없다
        'opacity-0 group-hover/lane:opacity-100 focus-visible:opacity-100',
        dir === 'prev' ? 'left-0' : 'right-0',
        // 끝에 닿으면 사라지되 **자리는 지운다** — 눌러도 아무 일 없는 버튼을 남기지 않는다
        hidden && 'pointer-events-none !opacity-0',
      )}
    >
      {dir === 'prev' ? '‹' : '›'}
    </button>
  );
}

/**
 * 가로 캐로셀 — 스크롤 컨테이너는 **그대로 두고** 버튼과 가장자리 신호만 얹는다.
 *
 * ★ 캐로셀 라이브러리를 들이지 않는다. 어려운 부분(카드 경계 정렬)은 `scroll-snap` 이 이미 하고,
 *   드래그·터치·트랙패드가 공짜로 따라온다. 새로 쓰는 것은 "한 화면 밀기"와 "지금 어디쯤인가"뿐이다.
 * ★ **`scroll-pl-4` 가 필요하다.** 없으면 스냅이 `px-4` 를 몰라 쉬는 자리의 `scrollLeft` 가
 *   **0 이 아니라 16** 이 된다(실측). 그 상태로 `scrollLeft > 0` 을 "왼쪽으로 더 갈 수 있다"로 읽으면
 *   **왼쪽 화살표가 영영 안 사라진다.**
 * ★ 가장자리 **흐림(mask)이 늘 보이는 신호**다. 1280px(본문 최대 폭)에서 가로형 카드가 정확히
 *   5장 꽉 차고 6번째가 **4px** 만 비쳐(실측), "살짝 잘린 카드"가 사실상 사라진다 —
 *   화살표가 hover 에서만 뜨므로 그 자리를 흐림이 메운다.
 * ★ 이름 있는 그룹(`group/lane`)을 쓴다. 카드 `<a>` 가 이미 `group` 을 달고 있어(죽은 클래스지만)
 *   이름 없는 `group-hover` 를 쓰면 나중에 카드 쪽에 호버 효과를 넣는 순간 서로 가린다.
 */
function Carousel({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ scrollable: false, start: true, end: true });

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdge({ scrollable: max > 1, start: el.scrollLeft <= 1, end: el.scrollLeft >= max - 1 });
  }, []);

  // 카드가 늘거나 창이 좁아지면 넘침 여부가 바뀐다 — 리사이즈까지 본다
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sync]);

  // ★ 한 화면씩. 카드 수를 세지 않는다 — 스냅이 카드 경계로 맞춰 준다(실측: 0 → 1260)
  const page = (dir: -1 | 1) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth, behavior: 'smooth' });

  return (
    <div className="group/lane relative">
      <div
        ref={ref}
        onScroll={sync}
        className={cn(
          '-mx-4 flex snap-x scroll-pl-4 gap-3 overflow-x-auto px-4 pt-1 pb-3',
          // 스크롤 막대는 숨긴다(운영자 요청). 두 줄이 **각자 다른 엔진**을 덮으므로 함께 쓴다 —
          // `scrollbar-width` 는 표준이고 `::-webkit-scrollbar` 는 사파리·구형 웹킷용이다.
          // ★ 밀 수 있다는 신호가 막대 하나 줄었다 — 아래 흐림과 화살표가 그 몫을 진다
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          // 더 있는 쪽만 흐린다. 양쪽을 한 식으로 쓴다 — 가운데에 있으면 둘 다 흐려진다
          !edge.start &&
            !edge.end &&
            '[mask-image:linear-gradient(to_right,transparent,black_40px,black_calc(100%-40px),transparent)]',
          !edge.start && edge.end && '[mask-image:linear-gradient(to_right,transparent,black_40px)]',
          edge.start && !edge.end && '[mask-image:linear-gradient(to_right,black_calc(100%-40px),transparent)]',
        )}
      >
        {children}
      </div>
      {edge.scrollable ? (
        <>
          <LaneArrow dir="prev" onClick={() => page(-1)} hidden={edge.start} />
          <LaneArrow dir="next" onClick={() => page(1)} hidden={edge.end} />
        </>
      ) : null}
    </div>
  );
}

function Card({
  href,
  seed,
  cover,
  kind,
  title,
  meta,
  date,
  portrait = false,
  grid = false,
  footer,
}: {
  href?: string;
  seed: string;
  cover: string | null;
  kind: string;
  title: string;
  meta?: string;
  date: string;
  portrait?: boolean;
  grid?: boolean;
  footer?: React.ReactNode;
}) {
  const body = (
    <>
      <Cover src={cover} seed={seed} kind={kind} portrait={portrait} />
      <p className="mt-2 line-clamp-2 font-medium text-[14px] text-slate-900 leading-snug">{title}</p>
      <p className="mt-1 text-[12px] text-slate-500">
        {meta ? `${meta} · ` : ''}
        {date}
      </p>
    </>
  );
  return (
    <div className={cn(grid ? 'w-full' : cn('shrink-0 snap-start', portrait ? 'w-[164px]' : 'w-[240px]'))}>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className="group block">
          {body}
        </a>
      ) : (
        body
      )}
      {footer}
    </div>
  );
}

/**
 * 섹션 하나. 위를 1px 선으로 가른다.
 * 0건이면 **빈칸이 아니라 안내 문구**다 — 기사처럼 주기가 긴 항목은 첫 달의 0건이 정상이고,
 * 빈칸을 보이면 "안 해 주고 있다"로 읽힌다.
 * ★ 문구에 **건수를 쓰지 않는다**(운영자 방침 2026-10-01). "월 1건"처럼 수를 박아 두면 계약·상품이
 *   바뀔 때마다 화면이 거짓이 되고, 고객은 그 수를 약속으로 읽는다. 무엇을 하는지만 말한다.
 */
function Section({
  section,
  note,
  rows,
  hidden,
}: {
  section: SectionKey;
  /**
   * 이 섹션에서 **우리가 매달 무엇을 하는가**. ★ 콘텐츠가 있어도 보인다 (운영자 결정 2026-10-01).
   * 원래는 0건일 때만 나오는 문구였는데, 고객은 "이번 달에 무엇이 올라왔나"만이 아니라
   * **무엇을 해 주는 서비스인가**를 같이 읽는다 — 그래서 상시 설명으로 올렸다.
   * ★ 문장은 **한 벌**이다. 빈 상태용과 설명용으로 가르면 둘이 어긋난다.
   * ★ 건수를 쓰지 않는다(`월 1건`·`2편`) — 계약·상품이 바뀌면 화면이 거짓이 되고,
   *   고객은 그 수를 약속으로 읽는다 (261001-client-works-report).
   */
  note: string;
  rows: React.ReactNode;
  /** 레일에 못 실어 넘어간 개수. 0 이면 전체 보기를 두지 않는다 — 누르면 같은 것이 나오는 버튼은 군더더기다 */
  hidden: number;
}) {
  const { label, Icon, color } = SECTION_META[section];
  return (
    <section className="border-slate-200 border-t pt-5">
      <div className="mb-1 flex items-center justify-between gap-4">
        <h2 className="flex items-center gap-2 font-bold text-lg text-slate-900">
          {/* 아이콘만 색을 갖는다 — 글자까지 물들이면 다섯 섹션이 서로 소리를 지른다 */}
          <Icon className={cn('h-[1.1em] w-[1.1em] shrink-0', color)} />
          {label}
        </h2>
        {hidden > 0 ? (
          <Link
            to="/app/works/$section"
            params={{ section }}
            // 보고서 주소의 `?brand=` 를 그대로 넘긴다 — 전체 보기도 같은 브랜드다
            search={true}
            className="rounded border border-slate-200 px-2 py-1 text-slate-600 text-xs tabular-nums hover:border-slate-400 hover:text-slate-900"
          >
            전체 보기 +{hidden}
          </Link>
        ) : null}
      </div>
      {/* 0건이면 이 줄 하나가 섹션의 전부다 — 빈칸을 보이면 "안 해 주고 있다"로 읽힌다 */}
      <p className={cn('text-[13px] text-slate-500', rows ? 'mb-3' : 'py-1')}>{note}</p>
      {rows}
    </section>
  );
}

function PostCard({ post, showQuery, grid }: { post: PostView; showQuery: boolean; grid: boolean }) {
  return (
    <Card
      grid={grid}
      href={post.url}
      seed={post.url}
      cover={post.coverUrl}
      kind="글"
      title={post.title}
      meta={showQuery && post.query ? post.query : undefined}
      date={dateText(post.publishedOn)}
    />
  );
}

/**
 * 언론 보도 한 건 — **기사 미리보기**다(포털 메인의 기사 꼴: 큰 사진 + 제목 + 본문 첫머리). 운영자 결정 2026-10-10.
 * ★ 다른 산출물처럼 작은 카드를 레일에 늘어놓지 않는다 — 기사는 월 1건, 많아야 2~3건이라 작은 카드는 빈약하고,
 *   고객이 읽는 것은 "기사가 났다"가 아니라 **어떤 기사가 났나**다. 그래서 전부 크게, 세로로 쌓는다.
 * ★ 사진은 **4:3 · 위쪽 기준**으로 자른다. 언론사 og 사진이 대개 세로·정사각의 원장 사진이라(실측 2026-10-10:
 *   700², 500×565, 300×404) 16:9 로 자르면 얼굴이 잘린다.
 * ★ 제목·사진·첫머리는 언론사 og 가 먼저, 없으면 우리 원고다 — 서버가 골라 보내고 화면은 출처를 모른다.
 */
function PressPreview({ item }: { item: PressView }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noreferrer"
      className="group/press flex flex-col gap-4 py-5 first:pt-1 last:pb-1 md:flex-row md:items-start md:gap-6"
    >
      <div className="w-full shrink-0 md:w-[28%] md:max-w-[320px]">
        <Cover
          src={item.coverUrl}
          seed={item.url}
          kind="기사"
          portrait={false}
          aspect="aspect-[4/3]"
          focus="object-[50%_20%]"
        />
      </div>
      <div className="min-w-0 flex-1 md:pt-1">
        <p className="text-[12px] text-slate-500">
          <span className="font-semibold text-slate-700">{item.outlet || '언론사'}</span>
          {' · '}
          {item.publishedOn.replaceAll('-', '.')}
        </p>
        <p
          className={cn(
            'mt-1.5 line-clamp-2 font-bold text-[19px] text-slate-900 leading-snug',
            // ★ skip-ink 를 끈다 — 한글 받침이 밑줄을 글자마다 끊는다(ExternalLink 와 같은 이유)
            'decoration-slate-400 underline-offset-4 [text-decoration-skip-ink:none] group-hover/press:underline',
          )}
        >
          {item.title}
        </p>
        {item.excerpt ? (
          <p className="mt-2 line-clamp-3 text-[14px] text-slate-600 leading-relaxed">{item.excerpt}</p>
        ) : null}
      </div>
    </a>
  );
}

function ReviewCard({ item, grid }: { item: ReviewView; grid: boolean }) {
  return (
    <Card
      grid={grid}
      href={item.url}
      seed={item.url}
      cover={item.coverUrl}
      kind="블로그"
      title={item.title}
      meta={item.platform}
      date={dateText(item.publishedOn)}
    />
  );
}

/**
 * 영상 한 편이 **여러 장의 카드**가 된다 — 원본 + 올라간 플랫폼마다 하나씩.
 * ★ 같은 영상을 다섯 곳에 올렸다는 것이 이 상품의 내용이므로(브로슈어 SERVICE 05), 한 줄에 링크를
 *   모아 두면 **한 편으로만 보인다.** 카드로 펼치면 실제로 한 일의 양이 드러난다 (운영자 결정 2026-10-01).
 * ★ 영상은 쇼츠라 **세로 카드**다.
 * ★ 원본이 아직 없으면 그 카드를 **만들지 않는다** — 누를 수 없는 카드는 고장으로 읽힌다.
 *   (대신 영상이 0편일 때의 안내 문구가 무엇을 하는지 말한다.)
 * ★ **원본 카드에 `download` 속성을 쓰지 않는다** — 원본은 외부 스토리지(교차 출처)라 브라우저가
 *   그 속성을 **무시하고 같은 탭에서 이동**한다(실측: Chrome 이 `storage.googleapis.com` 의 mp4 로
 *   이동했다). 내려받기는 어차피 그쪽 응답이 하는 일이므로, 모든 카드를 **새 탭**으로 통일한다 —
 *   리포트를 보던 자리를 잃지 않는다.
 */
interface VideoCardItem {
  key: string;
  video: VideoView;
  /** 플랫폼 카드면 그 플랫폼, 원본 카드면 null */
  platform: Platform | null;
  url: string;
}

/** 영상 목록을 카드 목록으로 편다. 영상 하나당 [원본, 플랫폼…] 순서다 */
function videoCards(videos: VideoView[]): VideoCardItem[] {
  return videos.flatMap((v) => [
    // ★ 계약 응답에 id 가 없다(고객에게 필요 없는 내부 식별자는 싣지 않는다) — 자연키로 묶는다
    ...(v.sourceUrl ? [{ key: `${v.title}-${v.publishedOn}-src`, video: v, platform: null, url: v.sourceUrl }] : []),
    ...v.posts.map((p) => ({
      key: `${v.title}-${v.publishedOn}-${p.platform}`,
      video: v,
      platform: p.platform,
      url: p.url,
    })),
  ]);
}

/** 펴 둔 카드를 다시 영상 단위로 묶는다 — `videoCards` 가 영상별로 연속이라 참조가 바뀌는 곳에서 끊는다 */
function byVideo(cards: VideoCardItem[]): { key: string; cards: VideoCardItem[] }[] {
  const rows: { key: string; video: VideoView; cards: VideoCardItem[] }[] = [];
  for (const c of cards) {
    const last = rows.at(-1);
    if (last && last.video === c.video) last.cards.push(c);
    else rows.push({ key: c.key, video: c.video, cards: [c] });
  }
  return rows;
}

/**
 * 플랫폼 카드의 가운데에 얹는 상표.
 * ★ **한 영상의 카드 다섯 장은 썸네일이 전부 같다** — 같은 영상이기 때문이다(`videoCover`).
 *   그대로 두면 같은 그림이 레일에 반복돼 지루하고, 어느 카드가 어느 플랫폼인지도 작은 칩
 *   하나로만 갈린다. dim 위에 상표를 크게 얹으면 **카드마다 다른 그림**이 된다
 *   (운영자 결정 2026-10-01). ★ 원본 카드에는 얹지 않는다 — 그쪽은 플랫폼이 없다.
 * ★ 상표만 두지 않고 **칩의 글자를 남긴다** — 스레드·X 는 마크만으로 못 읽는 사람이 있다.
 */
const PLATFORM_ICON = {
  youtube: YouTubeIcon,
  instagram: InstagramIcon,
  x: XIcon,
  threads: ThreadsIcon,
  tiktok: TikTokIcon,
} as const satisfies Record<Platform, unknown>;

function VideoCard({ item, grid }: { item: VideoCardItem; grid: boolean }) {
  const { video, platform } = item;
  const label = platform ? PLATFORM_LABEL[platform] : '원본';
  const Mark = platform ? PLATFORM_ICON[platform] : null;
  return (
    <div className={cn(grid ? 'w-full' : 'w-[164px] shrink-0 snap-start')}>
      <a href={item.url} target="_blank" rel="noreferrer" className="block">
        <Cover
          src={videoCover(video)}
          seed={`${video.title}-${label}`}
          kind={label}
          portrait
          overlay={Mark ? <Mark className="h-auto w-[38%] drop-shadow" /> : undefined}
        />
        <p className="mt-2 line-clamp-2 font-medium text-[14px] text-slate-900 leading-snug">{video.title}</p>
        <p className="mt-1 text-[12px] text-slate-500">
          {platform ? label : '원본 내려받기'} · {dateText(video.publishedOn)}
        </p>
      </a>
    </div>
  );
}

/**
 * @param limit  섹션마다 레일에 실을 최대 건수. 넘치는 만큼은 **전체 보기**로 섹션 전용 페이지에 넘긴다.
 *               `undefined` 면 전부 싣는다(전용 페이지가 그렇다)
 * @param grid   레일 대신 그리드로 — 섹션 전용 페이지용
 * @param only   그 섹션 하나만 — 섹션 전용 페이지용
 * @param showQuery 글 카드에 담당 질문을 함께 보일지
 */
export function Deliverables({
  data,
  limit,
  grid = false,
  only,
  showQuery = true,
}: {
  /** 회차 머리글은 화면이 따로 그린다 — 여기는 섹션만 본다 */
  data: Omit<WorksView, 'cycle'>;
  limit?: number;
  grid?: boolean;
  only?: SectionKey;
  showQuery?: boolean;
}) {
  const cut = <T,>(xs: T[]): T[] => (limit === undefined ? xs : xs.slice(0, limit));
  const hid = <T,>(xs: T[]): number => (limit === undefined ? 0 : Math.max(0, xs.length - limit));
  const show = (k: SectionKey) => only === undefined || only === k;
  const lane = <T,>(xs: T[], render: (x: T) => React.ReactNode) =>
    xs.length === 0 ? null : <Lane grid={grid}>{xs.map(render)}</Lane>;
  const cards = videoCards(data.videos);
  // 홈페이지가 여럿이면 글 수도 합쳐 세야 "+N" 이 맞는다
  const postsHidden = data.homepages.reduce((a, h) => a + hid(h.posts), 0);

  return (
    <div className="space-y-6">
      {/* ① AI검색 최적화 홈페이지 — 글은 홈페이지에 **속하므로** 같은 섹션에서 그 도메인 아래에 */}
      {show('homepage') ? (
        <Section
          section="homepage"
          note="AI검색 최적화된 홈페이지를 제작하고 매월 글을 발행합니다"
          hidden={postsHidden}
          rows={
            data.homepages.length === 0
              ? null
              : data.homepages.map((h) => (
                  <div key={h.url} className="mb-4 last:mb-0">
                    {/* ★ 도메인은 이 섹션의 **머리글**이다 — 글 카드(14px)·행(15px)과 같은 크기면
                        무엇이 그 사이트이고 무엇이 그 안의 글인지 한눈에 갈리지 않는다 */}
                    <div className="flex items-baseline justify-between gap-4 py-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-[17px] text-slate-900">
                          {/* 저장은 절대 URL, 표시는 도메인만 — 같은 값을 두 벌 저장하지 않는다 */}
                          <ExternalLink href={h.url}>{hostText(h.url)}</ExternalLink>
                        </p>
                      </div>
                      <span className="shrink-0 text-slate-500 text-sm tabular-nums">
                        {h.postCount === 0 ? '' : `글 ${h.postCount}편`}
                      </span>
                    </div>
                    {h.posts.length === 0 ? (
                      <p className="py-3 text-slate-500 text-sm">
                        홈페이지에 올릴 AI검색 최적화 글을 준비하고 있습니다
                      </p>
                    ) : (
                      <Lane grid={grid}>
                        {cut(h.posts).map((x) => (
                          <PostCard key={x.url} post={x} showQuery={showQuery} grid={grid} />
                        ))}
                      </Lane>
                    )}
                  </div>
                ))
          }
        />
      ) : null}
      {/* ② 언론 보도 */}
      {show('press') ? (
        <Section
          section="press"
          note="매달 AI검색 최적화로 작성된 기사를 언론사에 배포합니다"
          hidden={hid(data.press)}
          rows={
            // ★ 레일도 그리드도 아니다 — 리포트·전용 페이지·운영자 상세 모두 같은 세로 목록이다(PressPreview 주석)
            data.press.length === 0 ? null : (
              <div className="divide-y divide-slate-100">
                {cut(data.press).map((x) => (
                  <PressPreview key={x.url} item={x} />
                ))}
              </div>
            )
          }
        />
      ) : null}
      {/* ③ SNS 영상 — 영상 한 편이 원본 + 플랫폼마다 카드 하나다 */}
      {show('video') ? (
        <Section
          section="video"
          note="매달 숏폼 영상을 제작해 주요 SNS 플랫폼에 배포합니다"
          hidden={hid(cards)}
          rows={
            grid ? (
              lane(cut(cards), (c) => <VideoCard key={c.key} item={c} grid />)
            ) : cards.length === 0 ? null : (
              // ★ 레일에서는 **영상 한 편 = 한 줄**이다(운영자 결정 2026-10-09). 여러 편을 한 레일에 이으면
              //   어디까지가 한 편인지 안 보인다. 줄마다 캐로셀은 그대로 둔다
              <div className="space-y-4">
                {byVideo(cut(cards)).map((row) => (
                  <Lane key={row.key} grid={false}>
                    {row.cards.map((c) => (
                      <VideoCard key={c.key} item={c} grid={false} />
                    ))}
                  </Lane>
                ))}
              </div>
            )
          }
        />
      ) : null}
      {/* ④ 블로그 — 홈페이지가 아니라 별도 블로그 플랫폼에 올라간다 */}
      {show('review') ? (
        <Section
          section="review"
          note="AI검색에 최적화된 블로그 글을 발행합니다"
          hidden={hid(data.reviews)}
          rows={lane(cut(data.reviews), (x) => <ReviewCard key={x.url} item={x} grid={grid} />)}
        />
      ) : null}
    </div>
  );
}
